import { api } from './index';

export type IeCategoryProjectBankMode = 'STAFF_SELECT' | 'USE_BUILDING_DEFAULT' | 'FIXED_ACCOUNT';

export interface IeCategory {
  id: string;
  buildingId: string;
  code: string;
  name: string;
  projectBankAccountMode?: IeCategoryProjectBankMode;
  projectBankAccountId?: string | null;
  projectBankAccount?: { id: string; name: string; accountType?: string; isDefault?: boolean } | null;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
}

export type ProjectBankDepositLiquidity = 'DEMAND' | 'TERM';
export type ProjectBankFundPurpose = 'OPERATIONS' | 'MAINTENANCE' | 'OTHER';

export interface ProjectBankAccount {
  id: string;
  buildingId: string;
  accountType: string;
  depositLiquidity?: ProjectBankDepositLiquidity | null;
  fundPurpose?: ProjectBankFundPurpose | null;
  name: string;
  openingCashBalance: number;
  openingTransferBalance: number;
  cashBalance: number;
  transferBalance: number;
  exportVatInvoice: boolean;
  notes?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankName?: string | null;
  bankBin?: string | null;
  isInternal: boolean;
  isActive: boolean;
  isDefault: boolean;
  creator?: { id: string; name: string };
}

export interface CreateActivityVoucherPayload {
  buildingId: string;
  categoryId: string;
  amount: number;
  paymentMethod: 'CASH' | 'BANK_TRANSFER';
  actualDate?: string;
  apartmentId?: string;
  /** Free-text căn hộ when not linked to master apartment. */
  apartmentLabel?: string;
  name?: string;
  reason?: string;
  counterpartyName?: string;
  projectBankAccountId?: string;
}

export interface CreateActivityVoucherResponse {
  voucherId: string;
  journalNumber: string;
  activityLedgerEntryId: string;
  projectBankAccountId: string;
  projectBankAccountName: string;
}

export interface ProjectBankAccountActionLog {
  id: string;
  buildingId: string;
  projectBankAccountId?: string | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  accountName?: string | null;
  accountType?: string | null;
  notes?: string | null;
  createdAt: string;
  creator?: { id: string; name: string; email?: string };
}

export interface ApartmentSurplusAccount {
  id: string;
  buildingId: string;
  apartmentId: string;
  packageName: string;
  accountType: string;
  balance: number;
  apartment?: { id: string; code: string; name?: string | null };
  creator?: { id: string; name: string };
  ledgerEntries?: IeLedgerEntry[];
}

export interface ApartmentSurplusAccountsSummary {
  totalBalance: number;
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApartmentSurplusAccountsListResponse {
  items: ApartmentSurplusAccount[];
  summary: ApartmentSurplusAccountsSummary;
}

export interface IeLedgerEntry {
  id: string;
  amount: number;
  cashAmount: number;
  transferAmount: number;
  description?: string | null;
  displayLabel?: string;
  voucherCode?: string | null;
  canViewVoucher?: boolean;
  voucherViewEntryId?: string | null;
  journalNumber?: string | null;
  actualDate: string;
  entryType: string;
  /** Cột Thu on tài khoản thu dư detail (from API). */
  ledgerCreditAmount?: number;
  /** Cột Chi on tài khoản thu dư detail (from API). */
  ledgerDebitAmount?: number;
  revenueCategory?: { code: string; name: string };
  expenditureCategory?: { code: string; name: string };
  creator?: { id: string; name: string };
  bill?: { receiptNumber?: string | null };
}

export interface IncomeExpenseSummaryReportRow {
  id: string;
  kind: 'section' | 'group' | 'detail';
  section: 'opening' | 'revenue' | 'expenditure' | 'closing';
  label: string;
  unit: string;
  previousPeriod: number;
  currentPeriod: number;
  depth: number;
}

export interface IncomeExpenseSummaryReportResponse {
  buildingId: string;
  currentPeriod: string;
  previousPeriod: string;
  rows: IncomeExpenseSummaryReportRow[];
}

export interface CashFlowSummaryReportRow {
  id: string;
  kind: 'section' | 'detail';
  label: string;
  openingBalance: number;
  income: number;
  expense: number;
  closingBalance: number;
  depth: number;
}

export interface CashFlowSummaryReportResponse {
  buildingId: string;
  collectionPeriod: string;
  currentPeriod: string;
  previousPeriod: string;
  rows: CashFlowSummaryReportRow[];
}

export type BuildingLedgerDetailSectionKey = 'CASH_IN' | 'CASH_OUT' | 'BANK_IN' | 'BANK_OUT';

export interface BuildingLedgerDetailLine {
  entryId: string;
  actualDate: string;
  typeName: string;
  voucherName: string;
  voucherReceiptRef: string | null;
  apartmentCode: string | null;
  apartmentCustomerCode: string | null;
  projectBankAccountId: string | null;
  projectAccountName: string;
  amount: number;
  description: string;
  performerName: string | null;
}

export interface BuildingLedgerDetailGroup {
  typeName: string;
  totalAmount: number;
  lines: BuildingLedgerDetailLine[];
}

export interface BuildingLedgerDetailSection {
  key: BuildingLedgerDetailSectionKey;
  title: string;
  totalAmount: number;
  groups: BuildingLedgerDetailGroup[];
}

export interface BuildingLedgerDetailReportResponse {
  buildingId: string;
  collectionPeriod: string;
  dateFrom: string;
  dateTo: string;
  sections: BuildingLedgerDetailSection[];
}

export interface ProjectLedgerRow {
  id: string;
  actualDate: string;
  performerName: string | null;
  typeName: string;
  voucherName: string;
  voucherReceiptRef: string | null;
  activityCode: string | null;
  apartmentCode: string | null;
  apartmentCustomerCode: string | null;
  expectedIncome: number;
  expectedExpenditure: number;
  bankIncome: number;
  bankExpense: number;
  cashIncome: number;
  cashExpense: number;
  description: string;
  entryType: string;
  paymentMethod: string | null;
  billId: string | null;
  viewType: 'fee_receipt' | 'offset_voucher' | 'surplus_voucher' | 'manual_voucher' | null;
  alternateViewType: 'surplus_receipt' | null;
}

export interface ProjectLedgerSummary {
  expectedIncome: number;
  expectedExpenditure: number;
  bankIncome: number;
  bankExpense: number;
  cashIncome: number;
  cashExpense: number;
}

export interface ProjectLedgerResponse {
  account: {
    id: string;
    name: string;
    /** Cuối kỳ TM theo khoảng ngày lọc */
    cashBalance: number;
    /** Cuối kỳ CK theo khoảng ngày lọc */
    transferBalance: number;
    /** Đầu kỳ TM (cuối ngày trước dateFrom) */
    openingCashBalance: number;
    /** Đầu kỳ CK */
    openingTransferBalance: number;
  };
  rows: ProjectLedgerRow[];
  /** Tổng theo bộ lọc (bảng) */
  summary: ProjectLedgerSummary;
  /** Tăng/giảm trong kỳ — toàn bộ ledger tài khoản, không lọc loại thu/chi */
  periodSummary?: ProjectLedgerSummary;
  performers: Array<{ id: string; name: string | null }>;
}

export interface ProjectLedgerFilters {
  apartmentSearch?: string;
  voucherSearch?: string;
  revenueCategoryId?: string;
  /** Filter FEE_COLLECTION by invoice fee category (e.g. THE003). */
  feeCategoryId?: string;
  expenditureCategoryId?: string;
  createdById?: string;
  dateFrom?: string;
  dateTo?: string;
  paymentForm?: 'CASH' | 'TRANSFER';
}

export interface ActivityLedgerRow {
  id: string;
  actualDate: string;
  createdAt: string;
  apartmentCode: string | null;
  apartmentName: string | null;
  addressLabel: string | null;
  categoryName: string;
  voucherName: string;
  voucherCode: string | null;
  billingPeriod: string | null;
  reason: string | null;
  proposalCode: string | null;
  amount: number;
  entryType: string;
  journalNumber: string | null;
  description: string | null;
  billReceiptNumber: string | null;
  performerName: string | null;
  voucherViewEntryId: string | null;
  canViewVoucher: boolean;
  cashAmount: number;
  transferAmount: number;
  paymentMethodLabel: string;
  activityVoucherId: string | null;
  canCancelManualVoucher: boolean;
  canEditManualVoucher: boolean;
}

export interface ActivityVoucherEditForm {
  voucherId: string;
  buildingId: string;
  categoryId: string | null;
  amount: number;
  paymentMethod: 'CASH' | 'BANK_TRANSFER';
  actualDate: string;
  apartmentId: string | null;
  apartmentCode?: string | null;
  apartmentName?: string | null;
  apartmentLabel: string | null;
  name: string;
  reason: string | null;
  counterpartyName: string | null;
  journalNumber: string | null;
  projectBankAccountId: string | null;
}

export interface ActivityVoucherActionLog {
  id: string;
  buildingId: string;
  voucherKind: 'REVENUE' | 'EXPENDITURE';
  voucherId: string;
  journalNumber: string | null;
  action: 'UPDATE' | 'CANCEL';
  notes: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  creator?: { id: string; name: string | null; email?: string | null };
}

export interface CancelledActivityVoucherRow {
  id: string;
  journalNumber: string | null;
  name: string;
  totalAmount: number;
  cancelledAt: string | null;
  orderDate: string;
  revenueCategory?: { name: string } | null;
  expenditureCategory?: { name: string } | null;
  cancelledBy?: { id: string; name: string | null } | null;
}

export interface ActivityLedgerSummary {
  totalAmount: number;
  totalCashAmount: number;
  totalTransferAmount: number;
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ActivityLedgerResponse {
  rows: ActivityLedgerRow[];
  summary: ActivityLedgerSummary;
}

export interface ActivityLedgerFilters {
  apartmentSearch?: string;
  voucherSearch?: string;
  categoryId?: string;
  dateFrom?: string;
  dateTo?: string;
  paymentForm?: 'CASH' | 'TRANSFER';
}

export interface IeImportBatch {
  id: string;
  buildingId: string;
  importType: 'REVENUE' | 'EXPENDITURE' | 'REVENUE_PLAN';
  fileName: string;
  fileUrl?: string | null;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors?: string[] | null;
  createdAt: string;
  creator?: { id: string; name: string };
}

export type IeImportType = 'REVENUE' | 'EXPENDITURE' | 'REVENUE_PLAN';

export const IE_IMPORT_SAMPLE_NAMES: Record<IeImportType, string> = {
  REVENUE: 'khoan_thu_import_mau.xlsx',
  EXPENDITURE: 'khoan_chi_import_mau.xlsx',
  REVENUE_PLAN: 'du_toan_thu_import_mau.xlsx',
};

export const incomeExpenseApi = {
  listRevenueCategories: (buildingId: string) =>
    api.get<IeCategory[]>(`/income-expense/revenue-categories?buildingId=${buildingId}`).then((r) => r.data),

  createRevenueCategory: (data: {
    buildingId: string;
    code: string;
    name: string;
    projectBankAccountId?: string | null;
    projectBankAccountMode?: IeCategoryProjectBankMode;
  }) =>
    api.post<IeCategory>('/income-expense/revenue-categories', data).then((r) => r.data),

  updateRevenueCategory: (
    id: string,
    data: {
      name?: string;
      isActive?: boolean;
      projectBankAccountId?: string | null;
      projectBankAccountMode?: IeCategoryProjectBankMode;
    },
  ) =>
    api.patch<IeCategory>(`/income-expense/revenue-categories/${id}`, data).then((r) => r.data),

  deleteRevenueCategory: (id: string) =>
    api.delete(`/income-expense/revenue-categories/${id}`),

  seedRevenueCategories: (buildingId: string) =>
    api.post<IeCategory[]>('/income-expense/revenue-categories/seed-defaults', { buildingId }).then((r) => r.data),

  listExpenditureCategories: (buildingId: string) =>
    api.get<IeCategory[]>(`/income-expense/expenditure-categories?buildingId=${buildingId}`).then((r) => r.data),

  createExpenditureCategory: (data: {
    buildingId: string;
    code: string;
    name: string;
    projectBankAccountId?: string | null;
    projectBankAccountMode?: IeCategoryProjectBankMode;
  }) =>
    api.post<IeCategory>('/income-expense/expenditure-categories', data).then((r) => r.data),

  updateExpenditureCategory: (
    id: string,
    data: {
      name?: string;
      isActive?: boolean;
      projectBankAccountId?: string | null;
      projectBankAccountMode?: IeCategoryProjectBankMode;
    },
  ) =>
    api.patch<IeCategory>(`/income-expense/expenditure-categories/${id}`, data).then((r) => r.data),

  deleteExpenditureCategory: (id: string) =>
    api.delete(`/income-expense/expenditure-categories/${id}`),

  seedExpenditureCategories: (buildingId: string) =>
    api.post<IeCategory[]>('/income-expense/expenditure-categories/seed-defaults', { buildingId }).then((r) => r.data),

  listProjectBankAccounts: (buildingId: string) =>
    api.get<ProjectBankAccount[]>(`/income-expense/project-bank-accounts?buildingId=${buildingId}`).then((r) => r.data),

  createProjectBankAccount: (data: Partial<ProjectBankAccount> & { buildingId: string; name: string }) =>
    api.post<ProjectBankAccount>('/income-expense/project-bank-accounts', data).then((r) => r.data),

  updateProjectBankAccount: (id: string, data: Partial<ProjectBankAccount>) =>
    api.patch<ProjectBankAccount>(`/income-expense/project-bank-accounts/${id}`, data).then((r) => r.data),

  deleteProjectBankAccount: (id: string) =>
    api.delete(`/income-expense/project-bank-accounts/${id}`).then((r) => r.data),

  getProjectBankAccountActionLogs: (
    buildingId: string,
    options?: { page?: number; limit?: number },
  ) => {
    const params = new URLSearchParams({ buildingId });
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));
    return api
      .get<import('./paginatedHistory').PaginatedHistoryResponse<ProjectBankAccountActionLog>>(
        `/income-expense/project-bank-accounts/action-logs?${params.toString()}`,
      )
      .then((r) => r.data);
  },

  getProjectBankAccount: (id: string) =>
    api.get<ProjectBankAccount & { ledgerEntries: IeLedgerEntry[] }>(`/income-expense/project-bank-accounts/${id}`).then((r) => r.data),

  listProjectBankAccountLedger: (accountId: string, filters: ProjectLedgerFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, String(value));
    });
    const query = params.toString();
    return api
      .get<ProjectLedgerResponse>(
        `/income-expense/project-bank-accounts/${accountId}/ledger${query ? `?${query}` : ''}`,
      )
      .then((r) => r.data);
  },

  listRevenueActivityLedger: (
    buildingId: string,
    filters: ActivityLedgerFilters = {},
    options: { allRows?: boolean; page?: number; limit?: number } = {},
  ) => {
    const params = new URLSearchParams({ buildingId });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, String(value));
    });
    if (options.allRows) params.set('all', 'true');
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    return api
      .get<ActivityLedgerResponse>(`/income-expense/activity-ledger/revenue?${params.toString()}`)
      .then((r) => r.data);
  },

  listExpenditureActivityLedger: (
    buildingId: string,
    filters: ActivityLedgerFilters = {},
    options: { allRows?: boolean; page?: number; limit?: number } = {},
  ) => {
    const params = new URLSearchParams({ buildingId });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, String(value));
    });
    if (options.allRows) params.set('all', 'true');
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    return api
      .get<ActivityLedgerResponse>(`/income-expense/activity-ledger/expenditure?${params.toString()}`)
      .then((r) => r.data);
  },

  previewRevenueVoucher: (data: CreateActivityVoucherPayload) =>
    api.post<{ html: string }>('/income-expense/revenue-vouchers/preview', data).then((r) => r.data),

  previewExpenditureVoucher: (data: CreateActivityVoucherPayload) =>
    api.post<{ html: string }>('/income-expense/expenditure-vouchers/preview', data).then((r) => r.data),

  createRevenueVoucher: (data: CreateActivityVoucherPayload) =>
    api.post<CreateActivityVoucherResponse>('/income-expense/revenue-vouchers', data).then((r) => r.data),

  createExpenditureVoucher: (data: CreateActivityVoucherPayload) =>
    api.post<CreateActivityVoucherResponse>('/income-expense/expenditure-vouchers', data).then((r) => r.data),

  cancelRevenueVoucherByLedgerEntry: (ledgerEntryId: string) =>
    api
      .post<{ voucherId: string; journalNumber: string; status: string }>(
        `/income-expense/revenue-vouchers/cancel-by-ledger-entry/${ledgerEntryId}`,
      )
      .then((r) => r.data),

  cancelExpenditureVoucherByLedgerEntry: (ledgerEntryId: string) =>
    api
      .post<{ voucherId: string; journalNumber: string; status: string }>(
        `/income-expense/expenditure-vouchers/cancel-by-ledger-entry/${ledgerEntryId}`,
      )
      .then((r) => r.data),

  cancelFeeCollectionBillByLedgerEntry: (ledgerEntryId: string) =>
    api
      .post<{ billId: string; receiptNumber: string | null; receiptLabel: string; status: string }>(
        `/income-expense/fee-collection-bills/cancel-by-ledger-entry/${ledgerEntryId}`,
      )
      .then((r) => r.data),

  listCancelledRevenueVouchers: (buildingId: string) =>
    api
      .get<CancelledActivityVoucherRow[]>(
        `/income-expense/revenue-vouchers/cancelled?buildingId=${buildingId}`,
      )
      .then((r) => r.data),

  listCancelledExpenditureVouchers: (buildingId: string) =>
    api
      .get<CancelledActivityVoucherRow[]>(
        `/income-expense/expenditure-vouchers/cancelled?buildingId=${buildingId}`,
      )
      .then((r) => r.data),

  listRevenueVoucherActionLogs: (
    buildingId: string,
    options?: { page?: number; limit?: number },
  ) => {
    const params = new URLSearchParams({ buildingId });
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));
    return api
      .get<import('./paginatedHistory').PaginatedHistoryResponse<ActivityVoucherActionLog>>(
        `/income-expense/revenue-vouchers/action-logs?${params.toString()}`,
      )
      .then((r) => r.data);
  },

  listExpenditureVoucherActionLogs: (
    buildingId: string,
    options?: { page?: number; limit?: number },
  ) => {
    const params = new URLSearchParams({ buildingId });
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));
    return api
      .get<import('./paginatedHistory').PaginatedHistoryResponse<ActivityVoucherActionLog>>(
        `/income-expense/expenditure-vouchers/action-logs?${params.toString()}`,
      )
      .then((r) => r.data);
  },

  getRevenueVoucherForEdit: (ledgerEntryId: string) =>
    api
      .get<ActivityVoucherEditForm>(`/income-expense/revenue-vouchers/for-edit/${ledgerEntryId}`)
      .then((r) => r.data),

  getExpenditureVoucherForEdit: (ledgerEntryId: string) =>
    api
      .get<ActivityVoucherEditForm>(`/income-expense/expenditure-vouchers/for-edit/${ledgerEntryId}`)
      .then((r) => r.data),

  updateRevenueVoucher: (voucherId: string, data: CreateActivityVoucherPayload) =>
    api.patch<{ voucherId: string; journalNumber: string }>(
      `/income-expense/revenue-vouchers/${voucherId}`,
      data,
    ).then((r) => r.data),

  updateExpenditureVoucher: (voucherId: string, data: CreateActivityVoucherPayload) =>
    api.patch<{ voucherId: string; journalNumber: string }>(
      `/income-expense/expenditure-vouchers/${voucherId}`,
      data,
    ).then((r) => r.data),

  listApartmentSurplusAccounts: (
    buildingId: string,
    search?: string,
    options: { page?: number; limit?: number } = {},
  ) => {
    const params = new URLSearchParams({ buildingId });
    if (search?.trim()) params.set('search', search.trim());
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    return api
      .get<ApartmentSurplusAccountsListResponse>(
        `/income-expense/apartment-surplus-accounts?${params.toString()}`,
      )
      .then((r) => r.data);
  },

  getApartmentSurplusAccount: (apartmentId: string) =>
    api
      .get<ApartmentSurplusAccount>(`/income-expense/apartment-surplus-accounts/by-apartment/${apartmentId}`)
      .then((r) => r.data),

  getApartmentSurplusAccountByCode: (buildingId: string, apartmentCode: string) =>
    api
      .get<ApartmentSurplusAccount>(
        `/income-expense/apartment-surplus-accounts/by-apartment-code/${encodeURIComponent(apartmentCode)}?buildingId=${buildingId}`,
      )
      .then((r) => r.data),

  listImportBatches: (buildingId: string, importType: IeImportType) =>
    api
      .get<IeImportBatch[]>(`/income-expense/imports/batches?buildingId=${buildingId}&importType=${importType}`)
      .then((r) => r.data),

  downloadImportSample: async (buildingId: string, importType: IeImportType) => {
    const response = await api.get(
      `/income-expense/imports/export-sample?buildingId=${buildingId}&importType=${importType}`,
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = IE_IMPORT_SAMPLE_NAMES[importType];
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  importRevenue: async (buildingId: string, file: File) => {
    const formData = new FormData();
    formData.append('buildingId', buildingId);
    formData.append('file', file);
    const response = await api.post('/income-expense/imports/revenue', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importExpenditure: async (buildingId: string, file: File) => {
    const formData = new FormData();
    formData.append('buildingId', buildingId);
    formData.append('file', file);
    const response = await api.post('/income-expense/imports/expenditure', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importRevenuePlan: async (buildingId: string, file: File) => {
    const formData = new FormData();
    formData.append('buildingId', buildingId);
    formData.append('file', file);
    const response = await api.post('/income-expense/imports/revenue-plans', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  fetchLedgerVoucherHtml: async (
    entryId: string,
    mode?: 'fee' | 'surplus',
  ): Promise<string> => {
    const params = mode ? `?mode=${mode}` : '';
    const response = await api.get(`/income-expense/ledger-entries/${entryId}/voucher.html${params}`, {
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data as string;
  },

  getLedgerVoucherHtmlUrl: (entryId: string, mode?: 'fee' | 'surplus'): string => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    const params = mode ? `?mode=${mode}` : '';
    return `${base}/income-expense/ledger-entries/${entryId}/voucher.html${params}`;
  },

  getSummaryReport: async (query: {
    buildingId: string;
    collectionPeriod: string;
  }): Promise<IncomeExpenseSummaryReportResponse> => {
    const params = new URLSearchParams({
      buildingId: query.buildingId,
      collectionPeriod: query.collectionPeriod,
    });
    const response = await api.get(`/income-expense/summary-report?${params.toString()}`);
    return response.data;
  },

  getCashFlowSummaryReport: async (query: {
    buildingId: string;
    collectionPeriod: string;
  }): Promise<CashFlowSummaryReportResponse> => {
    const params = new URLSearchParams({
      buildingId: query.buildingId,
      collectionPeriod: query.collectionPeriod,
    });
    const response = await api.get(
      `/income-expense/cash-flow-summary-report?${params.toString()}`,
    );
    return response.data;
  },

  getBuildingLedgerDetailReport: async (query: {
    buildingId: string;
    collectionPeriod: string;
  }): Promise<BuildingLedgerDetailReportResponse> => {
    const params = new URLSearchParams({
      buildingId: query.buildingId,
      collectionPeriod: query.collectionPeriod,
    });
    const response = await api.get(
      `/income-expense/building-ledger-detail-report?${params.toString()}`,
    );
    return response.data;
  },

  listProjectBankStatementPeriods: (projectBankAccountId: string) =>
    api
      .get<ProjectBankStatementPeriodSummary[]>(
        `/income-expense/project-bank-accounts/${projectBankAccountId}/statement-periods`,
      )
      .then((r) => r.data),

  listProjectBankStatementImportHistory: (projectBankAccountId: string) =>
    api
      .get<ProjectBankStatementImportLogRow[]>(
        `/income-expense/project-bank-accounts/${projectBankAccountId}/statement-import-history`,
      )
      .then((r) => r.data),

  getProjectBankStatementPeriod: (periodId: string) =>
    api
      .get<ProjectBankStatementPeriodDetail>(`/income-expense/project-bank-statement-periods/${periodId}`)
      .then((r) => r.data),

  importProjectBankStatement: async (
    projectBankAccountId: string,
    file: File,
    periodYear?: number,
    periodMonth?: number,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (periodYear) params.set('periodYear', String(periodYear));
    if (periodMonth) params.set('periodMonth', String(periodMonth));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.post(
      `/income-expense/project-bank-accounts/${projectBankAccountId}/statement-import${qs}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data as ProjectBankStatementPeriodDetail;
  },

  autoMatchProjectBankStatement: (periodId: string) =>
    api
      .post<ProjectBankStatementPeriodDetail>(
        `/income-expense/project-bank-statement-periods/${periodId}/auto-match`,
      )
      .then((r) => r.data),

  matchProjectBankStatementLine: (lineId: string, ledgerEntryId: string) =>
    api
      .post<ProjectBankStatementPeriodDetail>(`/income-expense/project-bank-statement-lines/${lineId}/match`, {
        ledgerEntryId,
      })
      .then((r) => r.data),

  unmatchProjectBankStatementLine: (lineId: string) =>
    api
      .post<ProjectBankStatementPeriodDetail>(`/income-expense/project-bank-statement-lines/${lineId}/unmatch`)
      .then((r) => r.data),

  ignoreProjectBankStatementLine: (lineId: string, notes?: string) =>
    api
      .post<ProjectBankStatementPeriodDetail>(`/income-expense/project-bank-statement-lines/${lineId}/ignore`, {
        notes,
      })
      .then((r) => r.data),

  restoreProjectBankStatementLine: (lineId: string) =>
    api
      .post<ProjectBankStatementPeriodDetail>(`/income-expense/project-bank-statement-lines/${lineId}/restore`)
      .then((r) => r.data),

  listProjectBankStatementSurplusGapOptions: (lineId: string) =>
    api
      .get<ProjectBankStatementSurplusGapOption[]>(
        `/income-expense/project-bank-statement-lines/${lineId}/surplus-gap-options`,
      )
      .then((r) => r.data),

  createProjectBankStatementSurplusCredit: (lineId: string, billId: string) =>
    api
      .post<ProjectBankStatementPeriodDetail>(
        `/income-expense/project-bank-statement-lines/${lineId}/surplus-credit`,
        { billId },
      )
      .then((r) => r.data),
};

export interface ProjectBankStatementSurplusGapOption {
  billId: string;
  receiptNumber: string | null;
  apartmentId: string | null;
  apartmentCode: string | null;
  netTransfer: number;
  gap: number;
  statementAmount: number;
}

export interface ProjectBankStatementImportLogRow {
  id: string;
  periodId: string | null;
  periodYear: number;
  periodMonth: number;
  sourceFileName: string;
  fileUrl: string | null;
  lineCount: number;
  uploadedAt: string;
  uploader?: { id: string; name: string } | null;
}

export interface ProjectBankStatementPeriodSummary {
  id: string;
  periodYear: number;
  periodMonth: number;
  periodFrom: string | null;
  periodTo: string | null;
  statementBankAccountNumber?: string | null;
  openingBalance: number | null;
  closingBalance: number | null;
  periodTotalWithdrawal?: number | null;
  periodTotalDeposit?: number | null;
  sourceFileName: string | null;
  uploadedAt: string;
  uploader?: { id: string; name: string } | null;
  lineCount: number;
}

export type ProjectBankStatementLineStatus = 'UNMATCHED' | 'MATCHED' | 'IGNORED';

export interface ProjectBankStatementLineRow {
  id: string;
  sortOrder: number;
  bankReference: string | null;
  transactionAt: string;
  valueDate: string | null;
  content: string;
  withdrawalAmount: number;
  depositAmount: number;
  runningBalance: number | null;
  direction: 'DEPOSIT' | 'WITHDRAWAL';
  amount: number;
  status: ProjectBankStatementLineStatus;
  matchedLedgerEntryId: string | null;
  notes: string | null;
  matchedLedger?: {
    ledgerEntryId: string;
    entryType: string;
    journalNumber: string | null;
    canCancelManualVoucher: boolean;
  } | null;
  /** True when CK amount exceeds net phiếu thu CK (cấn trừ) — show Thu dư action */
  hasSurplusGapAction?: boolean;
}

export interface ProjectBankStatementPeriodDetail {
  id: string;
  buildingId: string;
  projectBankAccount: { id: string; name: string; bankAccountNumber: string | null };
  periodYear: number;
  periodMonth: number;
  periodFrom: string | null;
  periodTo: string | null;
  statementBankAccountNumber: string | null;
  openingBalance: number | null;
  closingBalance: number | null;
  periodTotalWithdrawal: number | null;
  periodTotalDeposit: number | null;
  sourceFileName: string | null;
  uploadedAt: string;
  uploader?: { id: string; name: string } | null;
  summary: {
    lineCount: number;
    matchedInBank: number;
    ignoredInBank: number;
    unmatchedInBank: number;
    missedInSystem: number;
    missedInBank: number;
  };
  lines: ProjectBankStatementLineRow[];
  ledgerEntries: Array<ProjectLedgerRow & { matchedStatementLineId: string | null }>;
}
